import { useRef } from "react";
import { Chart as ChartJS, CategoryScale, ChartData } from "chart.js/auto";
import Annotation from "chartjs-plugin-annotation";
import { Bar, getElementAtEvent } from "react-chartjs-2";
import Color from "color";
import styled from "styled-components";
import { ColorAA } from "../constants";
import { useAATheme } from "../AAThemeContext";

ChartJS.register(CategoryScale);
ChartJS.register(Annotation);

export type Data = ChartData<"bar", Array<DataPeriod>, string>;

export type DataPeriod = {
  x: number;
  y: number;
  start: { moment?: number; time: string; timeStamp?: number };
  end: { moment?: number; time: string; timeStamp?: number };
  agentId?: string;
  functionName?: string;
  isError?: boolean;
};

const HistogramContainer = styled.div`
  width: 100%;
  height: 250px;
  background-color: var(--aa-surface-bg);
  padding: 20px;
  border-radius: 6px;
`;

const Histogram = ({
  data,
  onClickElement,
  id,
  xAxisTitle,
}: {
  data: ChartData<"bar", Array<DataPeriod>, string>;
  onClickElement?: (dataSetIndex: number, index: number) => void;
  id?: string;
  xAxisTitle?: string;
}) => {
  const chartRef = useRef();
  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (chartRef.current) {
      const element = getElementAtEvent(chartRef.current, e);
      if (element?.[0]?.index !== undefined) {
        onClickElement?.(element?.[0]?.datasetIndex, element?.[0]?.index);
      }
    }
  };

  const { isDarkMode } = useAATheme();
  const textColor = isDarkMode ? ColorAA.lightBeige : ColorAA.lightBlack;
  const mutedColor = Color(isDarkMode ? ColorAA.beige : ColorAA.grey)
    .alpha(0.8)
    .hexa();
  const tooltipBg = isDarkMode ? ColorAA.lightBlack : ColorAA.white;

  return (
    <HistogramContainer>
      <Bar
        key={id}
        ref={chartRef}
        data={data}
        onClick={handleClick}
        options={{
          maintainAspectRatio: false,
          animation: {
            duration: 0,
          },
          scales: {
            y: {
              title: {
                display: true,
                text: "Count",
                color: mutedColor,
              },
              beginAtZero: true,
              stacked: true,
              min: 0,
              border: {
                color: mutedColor,
              },
              grid: {
                color: mutedColor,
              },
              ticks: {
                stepSize: 1,
                maxTicksLimit: 4,
                color: mutedColor,
              },
            },
            x: {
              title: {
                display: !!xAxisTitle,
                text: xAxisTitle,
                color: mutedColor,
              },
              stacked: true,
              ticks: {
                maxTicksLimit: 3,
                color: mutedColor,
              },
              border: {
                color: mutedColor,
              },
              grid: {
                display: false,
              },
            },
          },
          plugins: {
            legend: {
              onClick: () => {},
              position: "bottom",
              labels: {
                color: textColor,
                font: {
                  family: "monospace",
                  size: 14,
                },
              },
            },
            tooltip: {
              enabled: true,
              titleColor: mutedColor,
              bodyColor: textColor,
              backgroundColor: tooltipBg,
              borderWidth: 0.4,
              borderColor: mutedColor,
              titleFont: {
                size: 14,
              },
              bodyFont: {
                family: "monospace",
                size: 14,
              },
              callbacks: {
                title: (context) => {
                  const dataPeriod = context[0]?.raw as DataPeriod;
                  return `${dataPeriod.start.time} - ${dataPeriod.end.time}`;
                },
                afterTitle: (context) => {
                  const dataPeriod = context[0]?.raw as DataPeriod;
                  return dataPeriod.start.moment === dataPeriod.end.moment
                    ? `Moment ${dataPeriod.start.moment}`
                    : `Moments ${dataPeriod.start.moment} - ${dataPeriod.end.moment} (${dataPeriod.y === 1 ? "1 moment" : `${dataPeriod.y} moments`})`;
                },
                label: (context) => {
                  return context.dataset.label;
                },
              },
            },
          },
        }}
      />
    </HistogramContainer>
  );
};

export default Histogram;
